import { useMemo } from 'react';
import { useApiQuery } from '../../../../common/infrastructure/hooks/useApiQuery';
import { endpointsMetadata } from '../../../../common/infrastructure/routes/endpointsMetadata';
import { IGroupClass, IGroupClassCategory, ICategorizedGroupClassesResponse } from '../models';

/**
 * Hook to fetch available group classes, organized by categories
 */
export const useGroupClassClasses = (studioId: number | undefined) => {
  const { data: responseData, isLoading, error } = useApiQuery<ICategorizedGroupClassesResponse>({
    queryKey: [endpointsMetadata.groupClasses.getAvailableClasses, studioId],
    endpoint: `${endpointsMetadata.groupClasses.getAvailableClasses}?studioId=${studioId}`,
    enabled: !!studioId,
    globalIsLoading: false,
  });

  // Categories from the response
  const categories = useMemo((): IGroupClassCategory[] => {
    if (!responseData?.categories) return [];
    return responseData.categories;
  }, [responseData?.categories]);

  // Classes from the response
  const classes = useMemo((): IGroupClass[] => {
    if (!responseData?.classes) return [];
    return responseData.classes;
  }, [responseData?.classes]);

  // Group classes by categoryId for easy consumption
  const classesByCategory = useMemo((): Record<number | 'uncategorized', IGroupClass[]> => {
    const grouped: Record<number | 'uncategorized', IGroupClass[]> = {
      uncategorized: [],
    };

    // Initialize groups for each category
    categories.forEach(cat => {
      grouped[cat.id] = [];
    });

    // The backend always ensures a "Други" catch-all category exists (last by sortOrder).
    // Classes whose category was deleted should fall into this catch-all, not into a
    // separate "Други услуги" uncategorized bucket.
    const fallbackCategoryId = categories.length > 0
      ? categories[categories.length - 1].id
      : null;

    // Group classes
    classes.forEach(cls => {
      if (cls.categoryId === null) {
        grouped.uncategorized.push(cls);
      } else if (grouped[cls.categoryId] !== undefined) {
        grouped[cls.categoryId].push(cls);
      } else if (fallbackCategoryId !== null) {
        // Category was deleted — treat the same as "no category" by routing to "Други"
        grouped[fallbackCategoryId].push(cls);
      } else {
        grouped.uncategorized.push(cls);
      }
    });

    return grouped;
  }, [classes, categories]);

  return {
    classes,
    categories,
    classesByCategory,
    isLoading,
    error,
    hasData: !!responseData && (responseData.classes?.length > 0 || responseData.categories?.length > 0),
  };
};
